import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Users, Palette, CalendarClock, Rocket, Plus, ChevronRight, ChevronLeft, Send, FlaskConical, Clock } from "lucide-react";
import AudienceStep from "./AudienceStep";
import ContentStep from "./ContentStep";
import ScheduleStep from "./ScheduleStep";
import CampaignDetail from "./CampaignDetail";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STEPS = [
  { label: "Audience", icon: Users },
  { label: "Content", icon: Palette },
  { label: "Schedule", icon: CalendarClock },
];

const STARTER_MJML = `<mjml>
  <mj-body background-color="#f2f4f6">
    <mj-section background-color="#ffffff" padding="28px" border-radius="10px">
      <mj-column>
        <mj-text font-size="22px" color="#111827" font-family="Helvetica" font-weight="700">Hello {{first_name}} 👋</mj-text>
        <mj-text color="#374151" font-size="15px" line-height="1.6">Your message goes here. Personalize it with merge tags like {{first_name}}.</mj-text>
        <mj-button background-color="#2563eb" color="#ffffff" href="https://example.com">Call to action</mj-button>
      </mj-column>
    </mj-section>
  </mj-body>
</mjml>`;

const STARTER_HTML = `<div style="font-family:Helvetica,Arial,sans-serif;max-width:600px;margin:0 auto;background:#ffffff;padding:28px;color:#111827">
  <h1 style="font-size:22px;margin:0 0 12px">Hello {{first_name}} 👋</h1>
  <p style="font-size:15px;line-height:1.6;color:#374151">Paste your own HTML email here — it is sent as-is with merge tags substituted.</p>
  <a href="https://example.com" style="display:inline-block;margin-top:16px;background:#2563eb;color:#fff;padding:12px 22px;border-radius:6px;text-decoration:none">Call to action</a>
</div>`;

const BLANK = {
  name: "", recipients: [],
  fromLocal: "", fromDomain: "", fromName: "", replyTo: "", subject: "", templateId: null, mjml: STARTER_MJML, contentType: "mjml", templateName: "",
  maxPerHour: 100, provider: "auto", sendMode: "now", scheduledAt: "", isDryRun: false,
};

function Stepper({ step }) {
  return (
    <div className="stepper" data-testid="wizard-stepper">
      {STEPS.map((s, i) => {
        const Icon = s.icon;
        const state = i < step ? "done" : i === step ? "active" : "todo";
        return (
          <div key={s.label} className="step-wrap">
            <div className={`step-node ${state}`} data-testid={`step-node-${i}`}><Icon size={15} /></div>
            <span className={`step-label ${state}`}>{s.label}</span>
            {i < STEPS.length - 1 && <div className={`step-bar ${i < step ? "done" : ""}`} />}
          </div>
        );
      })}
    </div>
  );
}

function CampaignList({ onNew, onOpen }) {
  const [rows, setRows] = useState([]);
  useEffect(() => { axios.get(`${API}/v1/campaigns`).then((r) => setRows(r.data)); }, []);
  const cls = (s) => s === "COMPLETED" ? "b-delivered" : s === "FAILED" ? "b-bounced" : s === "SCHEDULED" ? "b-deferred" : "b-sent";
  return (
    <div data-testid="campaign-list">
      <button className="btn" data-testid="new-campaign-btn" onClick={onNew} style={{ marginBottom: 16 }}><Plus size={15} /> New Campaign</button>
      <div className="panel"><div className="p-head"><Send size={15} /><h3>Campaigns</h3><span className="hint">{rows.length}</span></div>
        <div className="scroll"><table className="tbl">
          <thead><tr><th>Name</th><th>Status</th><th>Recipients</th><th>Sent</th><th>Created</th></tr></thead>
          <tbody data-testid="campaigns-tbody">
            {rows.length === 0 && <tr><td colSpan={5} className="empty">No campaigns yet — create your first.</td></tr>}
            {rows.map((c) => (
              <tr key={c.id} className="clickable" data-testid={`campaign-row-${c.id.slice(0, 8)}`} onClick={() => onOpen(c.id)}>
                <td className="mono-strong">{c.name} {c.isDryRun && <FlaskConical size={11} />}</td>
                <td><span className={`badge ${cls(c.status)}`}>{c.status}</span></td>
                <td>{c.totalRecipients}</td><td>{c.sentCount}</td>
                <td>{new Date(c.createdAt).toLocaleString()}</td>
              </tr>
            ))}
          </tbody>
        </table></div>
      </div>
    </div>
  );
}

export default function Campaigns() {
  const [screen, setScreen] = useState("list");
  const [detailId, setDetailId] = useState(null);
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(BLANK);
  const [banner, setBanner] = useState(null);
  const [launching, setLaunching] = useState(false);

  const patch = useCallback((p) => setForm((f) => ({ ...f, ...p })), []);
  const notify = (kind, text) => { setBanner({ kind, text }); setTimeout(() => setBanner(null), 6000); };

  const fromEmail = form.fromLocal && form.fromDomain ? `${form.fromLocal}@${form.fromDomain}` : "";
  const valid = [
    form.recipients.length > 0,
    !!(form.fromLocal && form.fromDomain && form.fromName.trim().length >= 2 && form.subject.trim() && form.mjml.trim()),
    form.sendMode !== "later" || !!form.scheduledAt,
  ];

  const estimate = useMemo(() => {
    const hrs = form.recipients.length / (form.maxPerHour || 1);
    if (hrs < 1 / 60) return "< 1 min";
    if (hrs < 1) return `~${Math.ceil(hrs * 60)} min`;
    return `~${hrs.toFixed(1)} h`;
  }, [form.recipients.length, form.maxPerHour]);

  const startNew = () => { setForm(BLANK); setStep(0); setScreen("new"); };

  const launch = async () => {
    setLaunching(true);
    try {
      const payload = {
        name: form.name.trim() || "Untitled campaign",
        subject: form.subject, fromEmail, fromName: form.fromName,
        replyTo: form.replyTo || undefined,
        templateId: form.templateId || undefined,
        mjml: form.mjml,
        contentType: form.contentType || "mjml",
        recipients: form.recipients,
        settings: { maxPerHour: form.maxPerHour, provider: form.provider },
        isDryRun: form.isDryRun,
        ...(form.sendMode === "later" && form.scheduledAt ? { scheduledAt: new Date(form.scheduledAt).toISOString() } : {}),
      };
      const { data } = await axios.post(`${API}/v1/campaigns`, payload);
      notify("success", `Campaign ${data.status.toLowerCase()} → ${data.totalRecipients} recipients.`);
      setDetailId(data.id); setScreen("detail");
    } catch (e) {
      const d = e?.response?.data;
      notify("error", d?.details ? `MJML: line ${d.details[0].line} — ${d.details[0].message}` : (d?.message || e.message));
    } finally { setLaunching(false); }
  };

  if (screen === "list") return <CampaignList onNew={startNew} onOpen={(id) => { setDetailId(id); setScreen("detail"); }} />;
  if (screen === "detail") return <CampaignDetail campaignId={detailId} onBack={() => setScreen("list")} />;

  return (
    <div data-testid="campaign-wizard">
      {banner && <div className={`set-banner ${banner.kind}`} data-testid="wizard-banner">{banner.text}</div>}
      <div className="wiz-head">
        <input className="wiz-name" data-testid="campaign-name-input" value={form.name} onChange={(e) => patch({ name: e.target.value })} placeholder="Campaign name…" />
        <button className="btn ghost" data-testid="wizard-cancel-btn" onClick={() => setScreen("list")}>Cancel</button>
      </div>
      <Stepper step={step} />

      <div className="wiz-body">
        {step === 0 && <AudienceStep data={form} onChange={patch} />}
        {step === 1 && <ContentStep data={form} onChange={patch} firstRecipientVars={form.recipients[0]?.variables} />}
        {step === 2 && <ScheduleStep data={form} onChange={patch} />}
      </div>

      {step === 2 && (
        <div className="panel summary-card" data-testid="campaign-summary"><div className="p-head"><Rocket size={15} /><h3>Review & launch</h3></div>
          <div className="p-body"><div className="sum-grid">
            <div><span className="sk">Recipients</span><span className="sv" data-testid="summary-recipients">{form.recipients.length}</span></div>
            <div><span className="sk">Template</span><span className="sv">{form.templateName || "Custom MJML"}</span></div>
            <div><span className="sk">Speed</span><span className="sv">{form.maxPerHour}/hr</span></div>
            <div><span className="sk">Est. completion</span><span className="sv">{estimate}</span></div>
            <div><span className="sk">Provider</span><span className="sv">{form.provider === "auto" ? "Auto-Route" : form.provider}</span></div>
            <div><span className="sk">Start</span><span className="sv">{form.sendMode === "later" ? <><Clock size={11} /> {form.scheduledAt ? new Date(form.scheduledAt).toLocaleString() : "—"}</> : "Immediately"}</span></div>
          </div>
          {form.isDryRun && <div className="warn-inline" style={{ marginTop: 12 }}><FlaskConical size={13} /> Dry run — no real emails will be sent.</div>}
          </div>
        </div>
      )}

      <div className="wiz-nav">
        <button className="btn ghost" data-testid="wizard-back-btn" disabled={step === 0} onClick={() => setStep((s) => s - 1)}><ChevronLeft size={15} /> Back</button>
        {step < 2 ? (
          <button className="btn" data-testid="wizard-next-btn" disabled={!valid[step]} onClick={() => setStep((s) => s + 1)}>Next <ChevronRight size={15} /></button>
        ) : (
          <button className="btn launch" data-testid="launch-campaign-btn" disabled={!valid[0] || !valid[1] || !valid[2] || launching} onClick={launch}>
            <Rocket size={15} /> {launching ? "Launching…" : (form.sendMode === "later" ? "Schedule Campaign" : "Launch Campaign")}
          </button>
        )}
      </div>
    </div>
  );
}
